import { TabBar } from "@/design-system/index.ts"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { cn } from "@/primitives/utils"

/*
 * The shell's shape while Beacon asks who is signed in: the canvas, a sidebar of empty rows and the
 * inset panel with an empty header, and no words. Nobody may be signed in at all, so it promises
 * nothing but the frame; whichever arrives, the shell or the sign-in card, replaces it whole. On
 * the compact layout the frame is a phone's: the panel to the screen's edges under an empty
 * header, and the tab bar's six empty places along the bottom.
 */

/** Row widths for the sidebar's placeholder rows, fixed so every load draws the same frame. */
const rows = ["w-2/3", "w-1/2", "w-3/5", "w-2/5", "w-1/2", "w-3/5"] as const

/** The tab bar's places: Home, My tickets, Chat, Projects, Search and the avatar. */
const tabs = 6

/**
 * The frame: on `compact` the phone's; otherwise the sidebar collapsed to icons or open at 240px
 * as the real sidebar will open (`expanded`), so nothing jumps when it arrives.
 */
export function ShellSkeleton({ compact, expanded }: { compact: boolean; expanded: boolean }) {
  if (compact)
    return (
      <div aria-busy className="flex h-dvh flex-col bg-background">
        <div className="flex min-h-0 flex-1 flex-col px-safe pt-safe">
          <div className="flex h-12 shrink-0 items-center border-b border-border-subtle px-4">
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
        {/* The real bar, so its six places stand where its tabs will. */}
        <TabBar label="Tabs">
          {Array.from({ length: tabs }, (_, index) => (
            <li
              key={index}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5"
            >
              <span className="flex size-6 items-center justify-center">
                <Skeleton
                  className={index === tabs - 1 ? "size-6 rounded-full" : "size-5 rounded-md"}
                />
              </span>
              <span className="flex h-4 items-center">
                <Skeleton className="h-2 w-8" />
              </span>
            </li>
          ))}
        </TabBar>
      </div>
    )
  return (
    <div aria-busy className="flex h-svh bg-sidebar">
      <div className={cn("shrink-0 space-y-1 p-4", expanded ? "w-60" : "w-16")}>
        {rows.map((width, index) => (
          <div key={index} className="flex h-7 items-center gap-2 px-2">
            <Skeleton className="size-4 shrink-0" />
            {expanded && <Skeleton className={cn("h-3", width)} />}
          </div>
        ))}
      </div>
      <div className="m-2 ml-0 flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-background shadow-panel">
        <div className="flex h-12 shrink-0 items-center border-b border-border-subtle px-5">
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </div>
  )
}
